import { CircuitBoard, Wrench, MapPinned, ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { IMAGES, scrollToId } from "@/lib/site";

const Card = ({ image, icon: Icon, badge, title, desc, tall, testid, delay }) => (
  <Reveal delay={delay} className={tall ? "md:col-span-7 md:row-span-2" : "md:col-span-5"}>
    <div
      data-testid={testid}
      className="group relative h-full min-h-[320px] bg-panel border border-white/10 overflow-hidden rounded-sm hover:border-ember/40 transition-colors duration-300"
    >
      <div className="absolute inset-0">
        <img
          src={image}
          alt={title}
          className="w-full h-full object-cover opacity-35 group-hover:opacity-50 group-hover:scale-105 transition-all duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/70 to-transparent" />
      </div>
      <div className="relative z-10 p-8 flex flex-col justify-end h-full min-h-[320px]">
        <span className="absolute top-6 right-6 font-mono2 text-[10px] tracking-[0.25em] uppercase text-ember border border-ember/40 px-3 py-1 rounded-sm bg-obsidian/60 backdrop-blur-sm">
          {badge}
        </span>
        <Icon className="w-9 h-9 text-ember mb-4" strokeWidth={1.75} />
        <h3 className="font-display font-bold uppercase text-2xl sm:text-3xl tracking-tight leading-tight max-w-md">
          {title}
        </h3>
        <p className="mt-3 text-mist text-sm leading-relaxed max-w-md">{desc}</p>
        <button
          data-testid={`${testid}-cta`}
          onClick={() => scrollToId("#booking")}
          className="mt-5 inline-flex items-center gap-1.5 text-ember font-display font-bold uppercase tracking-wider text-base group-hover:gap-3 transition-all duration-200"
        >
          Book this service <ArrowUpRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  </Reveal>
);

export const Services = () => (
  <section id="services" data-testid="services-section" className="relative bg-obsidian py-16 sm:py-24 lg:py-32">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal>
        <Eyebrow>Core Services</Eyebrow>
        <div className="flex flex-wrap items-end justify-between gap-6">
          <h2 className="font-display font-extrabold uppercase text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none">
            Fixed right. <span className="text-ember">First time.</span>
          </h2>
          <p className="text-mist text-base max-w-sm">
            From elusive electrical faults to full brake jobs — master-level work, performed at your curb.
          </p>
        </div>
      </Reveal>

      <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-6">
        <Card
          tall
          delay={0}
          testid="service-card-diagnostics"
          image={IMAGES.diagnostics}
          icon={CircuitBoard}
          badge="Master Tech Specialty"
          title="Complex Electrical & Computer Diagnostics"
          desc="Check engine lights, parasitic battery drains, wiring harness faults, sensor troubleshooting & module diagnostics — traced with OEM-grade scanners and live data, not guesswork."
        />
        <Card
          delay={0.12}
          testid="service-card-maintenance"
          image={IMAGES.tools}
          icon={Wrench}
          badge="Driveway Ready"
          title="General Maintenance & Repairs"
          desc="Brakes, suspension, belts, batteries, fluids and routine service — done on your schedule."
        />
        <Card
          delay={0.24}
          testid="service-card-mobile"
          image={IMAGES.undercar}
          icon={MapPinned}
          badge="Scarborough & GTA"
          title="Mobile On-Site Convenience"
          desc="Diagnostics and repairs completed at your location. No towing fees, no waiting rooms."
        />
      </div>
    </div>
  </section>
);
